import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, ChevronDown, ChevronUp, Image as ImageIcon, LogOut, Plus, Trash2, Upload } from "lucide-react";
import { API, mediaUrl, youtubeId, youtubeThumb, youtubeThumbFallback } from "@/lib/api";
import "@/admin.css";

const TOKEN_KEY = "adminToken";
const CHUNK = 4 * 1024 * 1024;
const CATEGORIES = [
  ["motion", "Motion Graphics"],
  ["long", "Long Form"],
  ["short", "Short Form"],
];
const TEXT_FIELDS = [
  ["brand", "Brand / wordmark", "input"],
  ["monogram", "Hero monogram (2 letters)", "input"],
  ["plateName", "Name on the plate", "input"],
  ["workLabel", "Work section label", "input"],
  ["workTitle", "Work heading", "area"],
  ["aboutLabel", "About label", "input"],
  ["aboutTitle", "About heading", "area"],
  ["aboutBio", "About bio", "area"],
  ["aboutSecondary", "About second paragraph", "area"],
  ["aboutPhoto", "Portrait photo link (about section)", "input"],
  ["aboutFocus", "Fact — focus", "input"],
  ["aboutAlso", "Fact — also making", "input"],
  ["aboutCurrently", "Fact — currently", "input"],
  ["contactLabel", "Contact label", "input"],
  ["contactTitle", "Contact heading", "area"],
  ["contactIntro", "Contact intro", "area"],
  ["email", "Contact email", "input"],
  ["footerLeft", "Footer left", "input"],
  ["footerRight", "Footer right", "input"],
];

const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

async function uploadFile(file, token, onProgress) {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  let index = 0;
  for (let offset = 0; offset < file.size; offset += CHUNK) {
    const body = new FormData();
    body.append("upload_id", uploadId);
    body.append("index", index);
    body.append("file", file.slice(offset, offset + CHUNK), "part");
    await axios.post(`${API}/admin/upload/chunk`, body, authHeader(token));
    index += 1;
    onProgress(Math.min(99, Math.round(((offset + CHUNK) / file.size) * 100)));
  }
  const { data } = await axios.post(
    `${API}/admin/upload/complete`,
    { upload_id: uploadId, filename: file.name, content_type: file.type || "application/octet-stream" },
    authHeader(token)
  );
  onProgress(100);
  return data.url;
}

const Gate = ({ onPass }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onPass(data.token);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not sign in.");
    }
    setBusy(false);
  };
  return (
    <form className="gate" onSubmit={submit} data-testid="admin-login-form">
      <p className="gate-eyebrow">Studio</p>
      <h1>Editing panel</h1>
      <label>
        Password
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus data-testid="admin-password-input" />
      </label>
      {error && <p className="gate-error" data-testid="admin-login-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-login-button">{busy ? "Checking…" : "Unlock"} <ArrowUpRight size={15} /></button>
    </form>
  );
};

const PhotoPicker = ({ value, token, onPicked }) => {
  const [progress, setProgress] = useState(null);
  const [note, setNote] = useState("");

  const pick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      setNote("");
      setProgress(0);
      try {
        onPicked(await uploadFile(file, token, setProgress));
      } catch (e) {
        const detail = e.response?.data?.detail;
        setNote(typeof detail === "string" ? detail : "Upload failed. Please try again.");
      }
      setProgress(null);
    };
    input.click();
  };

  return (
    <div className="photo-picker">
      {value && <img src={mediaUrl(value)} alt="Portrait preview" data-testid="admin-portrait-preview" />}
      <button type="button" onClick={pick} data-testid="admin-portrait-upload"><Upload size={14} /> Upload photo</button>
      {value && <button type="button" className="danger" onClick={() => onPicked("")} data-testid="admin-portrait-clear"><Trash2 size={14} /></button>}
      {progress !== null && <span className="progress" data-testid="admin-portrait-progress">{progress}%</span>}
      {note && <span className="note">{note}</span>}
    </div>
  );
};

const ProjectRow = ({ project, index, total, token, portrait, onChange, onMove, onRemove }) => {
  const [progress, setProgress] = useState(null);
  const [note, setNote] = useState("");
  const videoRef = useRef(null);

  const pick = (accept, handler) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => input.files[0] && handler(input.files[0]);
    input.click();
  };

  const send = async (file, key) => {
    setNote("");
    setProgress(0);
    try {
      const url = await uploadFile(file, token, setProgress);
      onChange({ ...project, [key]: url });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setNote(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    }
    setProgress(null);
  };

  const grabFrame = async () => {
    setNote("");
    if (youtubeId(project.video)) {
      setNote("YouTube link detected — its thumbnail is used automatically. Upload an image only if you want a custom one.");
      return;
    }
    const video = videoRef.current;
    if (!video || !project.video) {
      setNote("Add a video first.");
      return;
    }
    try {
      video.currentTime = Math.min(1.5, (video.duration || 3) / 3);
      await new Promise((resolve, reject) => {
        video.onseeked = resolve;
        video.onerror = reject;
        setTimeout(resolve, 2500);
      });
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
      if (!blob) throw new Error("no blob");
      await send(new File([blob], "frame.jpg", { type: "image/jpeg" }), "image");
    } catch {
      setNote("Could not read a frame from that link — upload the video file or use your own image.");
    }
  };

  return (
    <div className="row" data-testid={`admin-project-${index}`}>
      <div className="row-head">
        <span className="row-no">{String(index + 1).padStart(2, "0")}</span>
        <div className="row-actions">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} aria-label="Move up" data-testid={`admin-project-${index}-up`}><ChevronUp size={15} /></button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} aria-label="Move down" data-testid={`admin-project-${index}-down`}><ChevronDown size={15} /></button>
          <button type="button" className="danger" onClick={() => onRemove(index)} aria-label="Remove" data-testid={`admin-project-${index}-remove`}><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="row-grid">
        <label>Title <input value={project.title} onChange={(e) => onChange({ ...project, title: e.target.value })} data-testid={`admin-project-${index}-title`} /></label>
        <label>Type / duration <input value={project.type} onChange={(e) => onChange({ ...project, type: e.target.value })} data-testid={`admin-project-${index}-type`} /></label>
        <label>Year <input value={project.year} onChange={(e) => onChange({ ...project, year: e.target.value })} data-testid={`admin-project-${index}-year`} /></label>
        <label>Role <input value={project.role} onChange={(e) => onChange({ ...project, role: e.target.value })} data-testid={`admin-project-${index}-role`} /></label>
        <label className="wide">Video link <input value={project.video} onChange={(e) => onChange({ ...project, video: e.target.value })} placeholder="https://… or upload a file" data-testid={`admin-project-${index}-video`} /></label>
        <label className="wide">Thumbnail link <input value={project.image} onChange={(e) => onChange({ ...project, image: e.target.value })} placeholder="https://… or upload / grab a frame" data-testid={`admin-project-${index}-image`} /></label>
      </div>
      <div className="row-tools">
        <button type="button" onClick={() => pick("video/*", (file) => send(file, "video"))} data-testid={`admin-project-${index}-upload-video`}><Upload size={14} /> Upload video</button>
        <button type="button" onClick={() => pick("image/*", (file) => send(file, "image"))} data-testid={`admin-project-${index}-upload-image`}><ImageIcon size={14} /> Upload thumbnail</button>
        <button type="button" onClick={grabFrame} data-testid={`admin-project-${index}-grab-frame`}>Grab frame from video</button>
        {progress !== null && <span className="progress" data-testid={`admin-project-${index}-progress`}>{progress}%</span>}
        {note && <span className="note">{note}</span>}
      </div>
      {project.video && (
        youtubeId(project.video) ? (
          <img
            className={portrait ? "row-preview portrait" : "row-preview"}
            src={mediaUrl(project.image) || youtubeThumb(youtubeId(project.video))}
            onError={(event) => { event.currentTarget.src = youtubeThumbFallback(youtubeId(project.video)); }}
            alt="YouTube thumbnail"
            data-testid={`admin-project-${index}-yt-preview`}
          />
        ) : (
          <video ref={videoRef} className={portrait ? "row-preview portrait" : "row-preview"} src={mediaUrl(project.video)} crossOrigin="anonymous" preload="metadata" muted playsInline poster={mediaUrl(project.image)} controls />
        )
      )}
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("text");
  const [category, setCategory] = useState("motion");
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/session`, authHeader(token)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    });
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => setContent(data));
  }, []);

  if (!token) return <div className="admin-shell gate-shell"><Gate onPass={setToken} /></div>;
  if (!content) return <div className="admin-shell"><p className="loading">Loading…</p></div>;

  const setText = (key) => (event) => setContent({ ...content, text: { ...content.text, [key]: event.target.value } });
  const list = content.projects[category] || [];
  const writeList = (next) => setContent({ ...content, projects: { ...content.projects, [category]: next } });

  const save = async () => {
    setStatus("Saving…");
    try {
      await axios.put(`${API}/admin/content`, content, authHeader(token));
      setStatus("Saved");
      setTimeout(() => setStatus(""), 2200);
    } catch (e) {
      setStatus(e.response?.status === 401 ? "Session expired — sign in again" : "Save failed");
    }
  };

  return (
    <div className="admin-shell" data-testid="admin-panel">
      <header className="admin-bar">
        <div>
          <p className="gate-eyebrow">Studio</p>
          <h1>Editing panel</h1>
        </div>
        <div className="admin-bar-actions">
          {status && <span className="status" data-testid="admin-status">{status}</span>}
          <a className="ghost" href="/" data-testid="admin-view-site">View site</a>
          <button type="button" className="primary" onClick={save} data-testid="admin-save-button">Save changes</button>
          <button type="button" className="ghost" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} data-testid="admin-logout-button"><LogOut size={15} /></button>
        </div>
      </header>

      <nav className="admin-tabs">
        {[["text", "Text"], ["videos", "Videos"], ["tools", "Tools"]].map(([key, label]) => (
          <button type="button" key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}>{label}</button>
        ))}
      </nav>

      {tab === "text" && (
        <section className="panel" data-testid="admin-text-panel">
          <p className="hint">Line breaks split the heading across lines. Wrap words in *stars* to make them the italic accent.</p>
          <div className="text-grid">
            {TEXT_FIELDS.map(([key, label, kind]) => (
              <label key={key} className={kind === "area" ? "wide" : ""}>
                {label}
                {kind === "area" ? (
                  <textarea rows={key === "aboutBio" ? 4 : 2} value={content.text[key] || ""} onChange={setText(key)} data-testid={`admin-text-${key}`} />
                ) : (
                  <input value={content.text[key] || ""} onChange={setText(key)} data-testid={`admin-text-${key}`} />
                )}
                {key === "aboutPhoto" && <PhotoPicker value={content.text.aboutPhoto} token={token} onPicked={(url) => setContent({ ...content, text: { ...content.text, aboutPhoto: url } })} />}
              </label>
            ))}
          </div>
        </section>
      )}

      {tab === "videos" && (
        <section className="panel" data-testid="admin-videos-panel">
          <div className="cat-switch">
            {CATEGORIES.map(([key, label]) => (
              <button type="button" key={key} className={category === key ? "active" : ""} onClick={() => setCategory(key)} data-testid={`admin-cat-${key}`}>{label} <span>{(content.projects[key] || []).length}</span></button>
            ))}
          </div>
          {list.map((project, index) => (
            <ProjectRow
              key={project.id || index}
              project={project}
              index={index}
              total={list.length}
              token={token}
              portrait={category === "short"}
              onChange={(next) => writeList(list.map((item, i) => (i === index ? next : item)))}
              onMove={(from, dir) => {
                const to = from + dir;
                if (to < 0 || to >= list.length) return;
                const next = [...list];
                [next[from], next[to]] = [next[to], next[from]];
                writeList(next);
              }}
              onRemove={(at) => writeList(list.filter((_, i) => i !== at))}
            />
          ))}
          <button type="button" className="add" onClick={() => writeList([...list, { id: `${Date.now()}`, title: "New project", type: "", year: "", role: "", video: "", image: "" }])} data-testid="admin-add-project"><Plus size={15} /> Add a clip</button>
        </section>
      )}

      {tab === "tools" && (
        <section className="panel" data-testid="admin-tools-panel">
          <p className="hint">The badge is the short two-letter mark shown in the circle.</p>
          {(content.tools || []).map((tool, index) => (
            <div className="tool-row" key={tool.id || `tool-${index}`} data-testid={`admin-tool-${index}`}>
              <label>Name <input value={tool.name} onChange={(e) => setContent({ ...content, tools: content.tools.map((item, i) => (i === index ? { ...item, name: e.target.value } : item)) })} data-testid={`admin-tool-${index}-name`} /></label>
              <label>Badge <input maxLength={3} value={tool.symbol} onChange={(e) => setContent({ ...content, tools: content.tools.map((item, i) => (i === index ? { ...item, symbol: e.target.value } : item)) })} data-testid={`admin-tool-${index}-symbol`} /></label>
              <button type="button" className="danger" onClick={() => setContent({ ...content, tools: content.tools.filter((_, i) => i !== index) })} data-testid={`admin-tool-${index}-remove`}><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" className="add" onClick={() => setContent({ ...content, tools: [...(content.tools || []), { id: `${Date.now()}`, name: "New tool", symbol: "Nt" }] })} data-testid="admin-add-tool"><Plus size={15} /> Add a tool</button>
        </section>
      )}
    </div>
  );
}
